import React from "react";
import { Row, Col, Card, CardBody, Input, Button, FormGroup, Label, CardHeader, CardTitle } from "reactstrap";
import { MdAdd, MdSearch, MdClose, MdBarChart, MdArrowBack, MdCheckBox, MdCheckBoxOutlineBlank, MdRefresh, MdFullscreen, MdSettings } from "react-icons/md";
import SubHeader from "components/Navbars/SubHeader";
import { useGetReports } from "api/reports/service";
import { useUpdateDashboard } from "api/dashboards/service";
import { useGetStatistics } from "api/statistics/service";

const AddPanelDrawer = ({ isOpen, onClose, isLightTheme, activeColor, dashboardId, existingReports = [] }) => {
  const [step, setStep] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedReport, setSelectedReport] = React.useState(null);
  const [panelFilters, setPanelFilters] = React.useState([]);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 992);
  const { data: reportsData, isLoading } = useGetReports('all', [], searchTerm);
  const { mutate: updateDashboard, isLoading: isUpdating } = useUpdateDashboard();

  const reports = reportsData?.results || [];

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedReport(null);
      setPanelFilters([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    // Initialize panel filters with the ones from the report
    let reportFilters = [];
    if (report.filters) {
      try {
        reportFilters = typeof report.filters === 'string' ? JSON.parse(report.filters) : report.filters;
      } catch (e) {
        reportFilters = [];
      }
    }
    setPanelFilters(Array.isArray(reportFilters) ? reportFilters : []);
    setStep(2);
  };

  const toggleEnumValue = (filterId, value) => {
    setPanelFilters(prev => prev.map(f => {
      if (f.id === filterId) {
        const currentValues = Array.isArray(f.value) ? f.value : [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...f, value: newValues };
      }
      return f;
    }));
  };

  const handleSave = () => {
    if (!selectedReport) return;

    // Parse existing reports if they are strings
    let currentReports = [];
    try {
      currentReports = Array.isArray(existingReports) ? existingReports : JSON.parse(existingReports || "[]");
    } catch (e) {
      currentReports = [];
    }

    // Map existing reports to the expected format for the update API
    const formattedExisting = currentReports.map(r => ({
      documentId: r.report_id || r.documentId,
      filters: typeof r.filters === 'string' ? JSON.parse(r.filters) : r.filters
    }));

    const newPanel = {
      documentId: selectedReport.documentId,
      filters: panelFilters
    };

    updateDashboard({
      id: dashboardId,
      data: { reports: [...formattedExisting, newPanel] }
    }, {
      onSuccess: () => {
        onClose();
      }
    });
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: isOpen ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
        zIndex: 10000, transition: 'all 300ms ease',
        display: 'flex', justifyContent: 'flex-end',
        pointerEvents: isOpen ? 'auto' : 'none',
        backdropFilter: isOpen ? 'blur(2px)' : 'none'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: isMobile ? '100%' : '50vw', height: '100%',
          backgroundColor: isLightTheme ? '#fff' : '#1e1e2f',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex', flexDirection: 'column',
          padding: '0px'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '25px', borderBottom: '1px solid rgba(136,136,136,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="d-flex align-items-center">
            {step === 2 && (
              <div
                onClick={() => setStep(1)}
                style={{ cursor: 'pointer', marginRight: '15px', color: '#1d8cf8', display: 'flex', alignItems: 'center' }}
              >
                <MdArrowBack size={24} />
              </div>
            )}
            <div>
              <h4 style={{ margin: 0, fontWeight: '800', fontSize: '18px', color: isLightTheme ? '#333' : '#fff' }}>
                {step === 1 ? 'ADD PANEL - SELECT REPORT' : 'CONFIGURE FILTERS'}
              </h4>
              <p style={{ margin: 0, fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>
                {step === 1 ? 'Select a report to continue' : `Configuring filters for: ${selectedReport?.name}`}
              </p>
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', opacity: 0.6 }}><MdClose size={24} /></div>
        </div>

        <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {step === 1 ? (
            <>
              <div style={{ position: 'relative', marginBottom: '20px' }}>
                <MdSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} size={20} />
                <Input
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '45px',
                    height: '45px',
                    background: isLightTheme ? '#f8f9fa' : 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(136,136,136,0.1)',
                    color: isLightTheme ? '#333' : '#fff'
                  }}
                />
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {isLoading ? (
                  <div className="text-center p-5"><div className="spinner-border text-info" /></div>
                ) : reports.length === 0 ? (
                  <div className="text-center p-5 opacity-50">No reports found</div>
                ) : (
                  <div>
                    <div style={{
                      display: 'flex',
                      padding: '12px 15px',
                      background: isLightTheme ? '#f0f3f5' : 'rgba(255,255,255,0.05)',
                      marginBottom: '10px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: isLightTheme ? '#666' : '#aaa',
                      textTransform: 'uppercase'
                    }}>
                      <div style={{ flex: 1 }}>Action</div>
                      <div style={{ flex: 1 }}>Id</div>
                      <div style={{ flex: 3 }}>Name</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {reports.map((report) => (
                        <div
                          key={report.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 15px',
                            background: isLightTheme ? '#fff' : 'rgba(255,255,255,0.02)',
                            borderBottom: '1px solid rgba(136,136,136,0.05)',
                            fontSize: '12px',
                            transition: 'all 0.2s',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleReportSelect(report)}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = isLightTheme ? '#f8f9fa' : 'rgba(255,255,255,0.05)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = isLightTheme ? '#fff' : 'rgba(255,255,255,0.02)'}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              className="btn-select-report"
                              style={{
                                padding: '4px 12px',
                                border: '1px solid #1d8cf8',
                                color: '#1d8cf8',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                textTransform: 'uppercase',
                                transition: 'all 0.2s'
                              }}
                            >
                              SELECT
                            </div>
                          </div>
                          <div style={{ flex: 1, color: isLightTheme ? '#888' : '#aaa' }}>{report.id}</div>
                          <div style={{ flex: 3, fontWeight: '500', color: isLightTheme ? '#333' : '#fff' }}>{report.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {panelFilters.length === 0 ? (
                <div className="text-center p-5 opacity-50">This report has no configurable filters</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {panelFilters.map((filter) => (
                    <div
                      key={filter.id}
                      style={{
                        padding: '15px',
                        background: isLightTheme ? '#f8f9fa' : 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(136,136,136,0.1)'
                      }}
                    >
                      <h6 style={{ marginBottom: '15px', color: isLightTheme ? '#1d8cf8' : '#fff', fontWeight: 'bold', fontSize: '14px' }}>
                        {filter.label} <span style={{ fontSize: '10px', opacity: 0.6, fontWeight: 'normal', color: '#888' }}>({filter.operator})</span>
                      </h6>

                      {filter.type === 'enum' && filter.enum_values && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          {filter.enum_values.map(val => {
                            const isChecked = Array.isArray(filter.value) && filter.value.includes(val);
                            return (
                              <div
                                key={val}
                                onClick={() => toggleEnumValue(filter.id, val)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  color: isLightTheme ? '#333' : '#bbb'
                                }}
                              >
                                {isChecked ? <MdCheckBox color="#1d8cf8" size={18} /> : <MdCheckBoxOutlineBlank size={18} />}
                                <span className="ml-2">{val}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {filter.type !== 'enum' && (
                        <Input
                          type={filter.type === 'date' ? 'date' : 'text'}
                          value={filter.value || ''}
                          onChange={(e) => setPanelFilters(prev => prev.map(f => f.id === filter.id ? { ...f, value: e.target.value } : f))}
                          style={{
                            background: isLightTheme ? '#fff' : 'rgba(0,0,0,0.2)',
                            color: isLightTheme ? '#333' : '#fff',
                            border: '1px solid rgba(136,136,136,0.2)'
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 'auto', padding: '20px', borderTop: '1px solid rgba(136,136,136,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <div
            onClick={onClose}
            style={{ padding: '8px 20px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#888' }}
          >
            CANCEL
          </div>
          {step === 2 && (
            <div
              onClick={handleSave}
              style={{
                padding: '8px 25px',
                background: '#1d8cf8',
                color: '#fff',
                cursor: !isUpdating ? 'pointer' : 'not-allowed',
                fontSize: '13px',
                fontWeight: '600',
                opacity: !isUpdating ? 1 : 0.5,
                transition: 'all 0.2s'
              }}
            >
              {isUpdating ? 'SAVING...' : 'SAVE PANEL'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomDashboard = ({ dashboardId, isLightTheme, activeColor }) => {
  const resolvedActiveColor = React.useMemo(() => {
    if (activeColor) return activeColor;
    const sidebarColor = document.querySelector(".sidebar")?.getAttribute("data");
    return sidebarColor || localStorage.getItem("activeColor") || "blue";
  }, [activeColor]);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const { data: statistics, isLoading: isStatisticsLoading } = useGetStatistics(dashboardId);
  const { mutate: updateDashboard } = useUpdateDashboard();
  const dashboardData = statistics;
  const [expandedReports, setExpandedReports] = React.useState(new Set());

  const toggleExpand = (reportId) => {
    setExpandedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) newSet.delete(reportId);
      else newSet.add(reportId);
      return newSet;
    });
  };

  const reports = dashboardData?.reports || [];
  const shouldShowAddPanel = !isStatisticsLoading && (isEditMode || reports.length === 0);

  const handleDeletePanel = (index) => {
    // We use index because documentId might not be unique if the same report is added twice
    const updatedReports = reports
      .filter((_, i) => i !== index)
      .map(r => ({
        documentId: r.report_id || r.documentId,
        filters: typeof r.filters === 'string' ? JSON.parse(r.filters) : r.filters
      }));

    updateDashboard({
      id: dashboardId,
      data: { reports: updatedReports }
    });
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const dashboardTitle = dashboardData?.name
    ? `${capitalize(dashboardData.name)} Dashboard`
    : "Custom Dashboard";

  return (
    <div className="content m-3">
      <SubHeader
        activeColor={resolvedActiveColor}
        back={"/dashboard"}
        title={dashboardTitle}
        link={false}
        right={
          <div
            onClick={() => setIsEditMode(!isEditMode)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isEditMode ? 'rgba(255,255,255,0.2)' : 'transparent',
              padding: '5px',
              borderRadius: 0
            }}
          >
            <MdSettings color="white" size={24} />
          </div>
        }
      />

      <Row>
        {isStatisticsLoading ? (
          <Col xs="12" className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <div className="text-center">
              <div className="spinner-border text-info" role="status" />
            </div>
          </Col>
        ) : shouldShowAddPanel && (
          <Col lg="3" md="6" className="mb-4">
            <Card
              className="card-modern"
              style={{
                cursor: 'pointer',
                border: '2px dashed rgba(29, 140, 248, 0.3)',
                background: isLightTheme ? 'rgba(29, 140, 248, 0.02)' : 'rgba(29, 140, 248, 0.05)',
                transition: 'all 0.3s ease',
                height: '500px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#1d8cf8';
                e.currentTarget.style.background = isLightTheme ? 'rgba(29, 140, 248, 0.05)' : 'rgba(29, 140, 248, 0.1)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(29, 140, 248, 0.3)';
                e.currentTarget.style.background = isLightTheme ? 'rgba(29, 140, 248, 0.02)' : 'rgba(29, 140, 248, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              onClick={() => setIsDrawerOpen(true)}
            >
              <CardBody className="d-flex flex-column align-items-center justify-content-center">
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'rgba(29, 140, 248, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <MdAdd size={24} color="#1d8cf8" />
                </div>
                <h3 style={{
                  fontWeight: '800',
                  fontSize: '15px',
                  color: isLightTheme ? '#333' : '#fff',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Add Dashboard Panel
                </h3>
              </CardBody>
            </Card>
          </Col>
        )}
        {!isStatisticsLoading && reports.map((report, index) => {
          const isExpanded = expandedReports.has(report.report_id);
          return (
            <Col key={index} lg={isExpanded ? "6" : "3"} md="12" className="mb-4" style={{ transition: 'all 0.3s ease' }}>
              <Card className="card-chart card-modern" style={{ height: '500px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <CardHeader className="d-flex align-items-center justify-content-between" style={{ padding: '15px 20px' }}>
                  <div className="d-flex align-items-center" style={{ flex: 1, overflow: 'hidden' }}>
                    <MdBarChart className="mr-2" color="#1d8cf8" style={{ flexShrink: 0 }} />
                    <CardTitle tag="h4" style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: isLightTheme ? '#333' : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {report.name}
                    </CardTitle>
                  </div>
                  <div className="d-flex gap-2">
                    <MdFullscreen
                      style={{ cursor: 'pointer', opacity: isExpanded ? 1 : 0.6, color: isExpanded ? '#1d8cf8' : 'inherit' }}
                      size={20}
                      onClick={() => toggleExpand(report.report_id)}
                    />
                    {isEditMode && (
                      <MdClose
                        style={{ cursor: 'pointer', color: '#ff5e5e' }}
                        size={20}
                        onClick={() => handleDeletePanel(index)}
                      />
                    )}
                  </div>
                </CardHeader>
                <CardBody style={{ flex: 1, padding: 0, position: 'relative' }}>
                  <iframe
                    title={report.name}
                    srcDoc={report.html}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'white'
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          );
        })}


      </Row>

      <AddPanelDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        isLightTheme={isLightTheme}
        activeColor={resolvedActiveColor}
        dashboardId={dashboardId}
        existingReports={dashboardData?.reports || []}
      />
    </div>
  );
};

export default CustomDashboard;
